import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'apps/anihis-portal/src/environments/environment';

export interface ClinicForSharingDto {
  uid: string;
  name: string;
  address?: string;
  isShared: boolean;
}

export interface GetClinicsForSharingResult {
  clinics?: ClinicForSharingDto[];
  hasNextPage?: boolean;
  pageNumber?: number;
  pageSize?: number;
  totalCount?: number;
}

export interface HealthRecordShareDto {
  shareUid: string;
  clinicUid: string;
  clinicName: string;
  clinicAddress?: string;
}

export interface GetHealthRecordVisibilityResult {
  visibleToAllClinics: boolean;
  shares: HealthRecordShareDto[];
  canManage: boolean;
}

export interface UpdateHealthRecordVisibilityCommand {
  visibleToAllClinics: boolean;
}

export interface ShareHealthRecordCommand {
  clinicUid: string;
}

@Injectable({ providedIn: 'root' })
export class ReportVisibilityService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  getVisibility(healthRecordUid: string): Observable<GetHealthRecordVisibilityResult> {
    return this.http.get<GetHealthRecordVisibilityResult>(
      `${this.baseUrl}/HealthRecords/${healthRecordUid}/Visibility`,
    );
  }

  updateVisibility(
    healthRecordUid: string,
    command: UpdateHealthRecordVisibilityCommand,
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/HealthRecords/${healthRecordUid}/Visibility`,
      command,
    );
  }

  shareHealthRecord(
    healthRecordUid: string,
    command: ShareHealthRecordCommand,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/HealthRecords/${healthRecordUid}/Share`,
      command,
    );
  }

  revokeShare(healthRecordUid: string, clinicUid: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/HealthRecords/${healthRecordUid}/Share/${clinicUid}`,
    );
  }

  getClinicsForSharing(
    healthRecordUid?: string,
    search?: string,
    pageNumber = 1,
    pageSize = 20,
  ): Observable<GetClinicsForSharingResult> {
    let params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('PageSize', pageSize);

    if (healthRecordUid) {
      params = params.set('HealthRecordUid', healthRecordUid);
    }
    if (search) {
      params = params.set('Search', search);
    }

    return this.http.get<GetClinicsForSharingResult>(
      `${this.baseUrl}/Users/Clinics/ForSharing`,
      { params },
    );
  }
}
